import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  FaUsers, 
  FaHandshake, 
  FaLeaf, 
  FaBuilding,
  FaGlobe,
  FaShieldAlt,
  FaRoad,
  FaTrain
} from 'react-icons/fa';

const ImpactSection: React.FC<{ language: 'ar' | 'en' }> = ({ language }) => {
  const isRTL = language === 'ar';
  const [activeTab, setActiveTab] = useState(0);

  const impactData = [
    {
      id: 'job-creation',
      icon: FaUsers,
      titleAr: 'توفير فرص العمل',
      titleEn: 'Job Opportunities',
      subtitleAr: 'دعم التنمية البشرية في كل موقع',
      subtitleEn: 'Supporting Human Development in Every Location',
      descriptionAr: 'تلتزم شركة مكة البركة بخلق فرص عمل حقيقية للمجتمعات المحيطة بمشروعاتها، بما يساهم في تحسين حياة آلاف الأسر.',
      descriptionEn: 'Mecca El Baraka is committed to creating real job opportunities for communities surrounding its projects, contributing to improving the lives of thousands of families.',
      achievementsAr: [
        'خلق أكثر من 3500 فرصة عمل مباشرة وغير مباشرة على مدار السنوات الماضية',
        'إعطاء الأولوية لتوظيف العمالة المحلية في جميع المواقع',
        'تمكين الشباب وتدريبهم على أحدث التقنيات',
        'تعزيز مشاركة المرأة في سوق العمل الهندسي',
        'توفير فرص تطوير وظيفي طويلة الأجل للموظفين',
        'دعم روح العمل الجماعي والمساواة بين أفراد الفريق'
      ],
      achievementsEn: [
        'Created more than 3,500 direct and indirect job opportunities over the past years',
        'Prioritizing local workforce employment in all locations',
        'Empowering youth and training them on latest technologies',
        'Enhancing women participation in the engineering job market',
        'Providing long-term career development opportunities for employees',
        'Supporting teamwork spirit and equality among team members'
      ],
      quoteAr: '"نحن لا نبني مشروعات فقط، بل نبني مستقبلًا أفضل للناس."',
      quoteEn: '"We don\'t just build projects, we build a better future for people."',
      quoteAuthorAr: '- فريق الإدارة',
      quoteAuthorEn: '- Management Team',
      image: 'https://images.unsplash.com/photo-1521791136064-7986c2920216?auto=format&fit=crop&w=800&q=80',
      color: 'from-blue-600 to-blue-700',
      bgColor: 'bg-blue-50'
    },
    {
      id: 'community-development',
      icon: FaHandshake,
      titleAr: 'تنمية المجتمع المحلي',
      titleEn: 'Local Community Development',
      subtitleAr: 'الشريك الحقيقي في التغيير',
      subtitleEn: 'The True Partner in Change',
      descriptionAr: 'لا يقتصر دورنا على البناء والتشييد، بل نسعى لإحداث أثر إيجابي ملموس في حياة المجتمعات المحيطة بمشاريعنا.',
      descriptionEn: 'Our role is not limited to construction and contracting, but we seek to create a tangible positive impact in the lives of communities surrounding our projects.',
      achievementsAr: [
        'تطوير وصيانة المدارس والمراكز الصحية',
        'رعاية المبادرات الاجتماعية والأنشطة الثقافية',
        'بناء علاقات دائمة مع قيادات المجتمع',
        'تنفيذ مشاريع تحسين الخدمات العامة (كهرباء، مياه، طرق)',
        'دعم البرامج الرياضية والترفيهية للشباب',
        'الاستجابة السريعة لاحتياجات المجتمع في الطوارئ'
      ],
      achievementsEn: [
        'Development and maintenance of schools and health centers',
        'Sponsoring social initiatives and cultural activities',
        'Building lasting relationships with community leaders',
        'Implementing public service improvement projects (electricity, water, roads)',
        'Supporting sports and recreational programs for youth',
        'Quick response to community needs in emergencies'
      ],
      quoteAr: '"معًا نزرع بذور التغيير ونصنع مجتمعًا أقوى."',
      quoteEn: '"Together we plant the seeds of change and create a stronger community."',
      quoteAuthorAr: '- قيادة المجتمع',
      quoteAuthorEn: '- Community Leadership',
      image: 'https://images.unsplash.com/photo-1578662996442-48f60103fc96?auto=format&fit=crop&w=800&q=80',
      color: 'from-green-600 to-green-700',
      bgColor: 'bg-green-50'
    },
    {
      id: 'environmental-responsibility',
      icon: FaLeaf,
      titleAr: 'المسؤولية البيئية والاستدامة',
      titleEn: 'Environmental Responsibility & Sustainability',
      subtitleAr: 'نبني اليوم لنحافظ على الغد',
      subtitleEn: 'We Build Today to Preserve Tomorrow',
      descriptionAr: 'تضع مكة البركة حماية البيئة في صميم أعمالها عبر تطبيق أفضل معايير الاستدامة في كل مشروع.',
      descriptionEn: 'Mecca El Baraka places environmental protection at the heart of its operations by applying the best sustainability standards in every project.',
      achievementsAr: [
        'غرس أكثر من 2000 شجرة في مواقع العمل',
        'إعادة تدوير مخلفات البناء واستخدام مواد صديقة للبيئة',
        'تنفيذ حملات توعية بالاستدامة البيئية للموظفين',
        'تطبيق حلول هندسية للحد من الانبعاثات',
        'الحفاظ على الموارد الطبيعية وتطويرها',
        'الالتزام التام بالمعايير البيئية المحلية والدولية'
      ],
      achievementsEn: [
        'Planting more than 2,000 trees in work sites',
        'Recycling construction waste and using eco-friendly materials',
        'Implementing environmental sustainability awareness campaigns for employees',
        'Applying engineering solutions to reduce emissions',
        'Preserving and developing natural resources',
        'Full commitment to local and international environmental standards'
      ],
      quoteAr: '"الاستدامة مسؤولية الجميع… ونحن في المقدمة."',
      quoteEn: '"Sustainability is everyone\'s responsibility… and we are at the forefront."',
      quoteAuthorAr: '- فريق البيئة',
      quoteAuthorEn: '- Environment Team',
      image: 'https://images.unsplash.com/photo-1500382017468-9049fed747ef?auto=format&fit=crop&w=800&q=80',
      color: 'from-emerald-600 to-emerald-700',
      bgColor: 'bg-emerald-50'
    },
    {
      id: 'infrastructure-development',
      icon: FaBuilding,
      titleAr: 'تطوير البنية التحتية للمجتمعات',
      titleEn: 'Community Infrastructure Development',
      subtitleAr: 'حجر الأساس للتنمية',
      subtitleEn: 'The Foundation Stone for Development',
      descriptionAr: 'تلتزم الشركة بالمساهمة في تطوير بنية تحتية متكاملة تلبي احتياجات الحاضر وتستوعب طموحات المستقبل.',
      descriptionEn: 'The company is committed to contributing to the development of integrated infrastructure that meets present needs and accommodates future aspirations.',
      achievementsAr: [
        'تنفيذ شبكات طرق ومواصلات حديثة',
        'تطوير مرافق المياه والصرف والكهرباء',
        'بناء جسور ومعابر حيوية تربط المجتمعات',
        'تحديث البنية التحتية الرقمية والاتصالات',
        'الاعتماد على أحدث التقنيات في التخطيط والتنفيذ',
        'الالتزام بالجودة ومعايير السلامة في كل خطوة'
      ],
      achievementsEn: [
        'Implementing modern road and transportation networks',
        'Developing water, sewage and electricity facilities',
        'Building vital bridges and crossings connecting communities',
        'Updating digital infrastructure and communications',
        'Relying on latest technologies in planning and implementation',
        'Commitment to quality and safety standards in every step'
      ],
      quoteAr: '"كل طريق نبنيه هو بداية لفرص جديدة وحياة أفضل."',
      quoteEn: '"Every road we build is the beginning of new opportunities and a better life."',
      quoteAuthorAr: '- فريق الهندسة',
      quoteAuthorEn: '- Engineering Team',
      image: 'https://images.unsplash.com/photo-1541888946425-d81bb19240f5?auto=format&fit=crop&w=800&q=80',
      color: 'from-purple-600 to-purple-700',
      bgColor: 'bg-purple-50'
    },
    {
      id: 'safety-protection',
      icon: FaShieldAlt,
      titleAr: 'السلامة المهنية وحماية العمال',
      titleEn: 'Occupational Safety & Worker Protection',
      subtitleAr: 'لأن الإنسان هو الأولوية',
      subtitleEn: 'Because Human Safety is the Priority',
      descriptionAr: 'سلامة فرق العمل أولوية لا تقبل التهاون، فنعتمد إجراءات صارمة لضمان بيئة عمل آمنة وصحية.',
      descriptionEn: 'Workforce safety is a priority that does not tolerate negligence, so we adopt strict procedures to ensure a safe and healthy work environment.',
      achievementsAr: [
        'تطبيق أعلى معايير السلامة العالمية',
        'تدريب مستمر لجميع الموظفين على إجراءات الوقاية',
        'توفير معدات سلامة شخصية متقدمة',
        'إجراء فحوصات صحية منتظمة للعاملين',
        'رصد المخاطر والتعامل الفوري مع أي حادث',
        'حملات توعية دورية لتعزيز ثقافة السلامة'
      ],
      achievementsEn: [
        'Applying highest global safety standards',
        'Continuous training for all employees on prevention procedures',
        'Providing advanced personal safety equipment',
        'Conducting regular health checkups for workers',
        'Monitoring risks and immediate handling of any incident',
        'Periodic awareness campaigns to enhance safety culture'
      ],
      quoteAr: '"سلامة كل فرد لدينا هي نجاح حقيقي لنا."',
      quoteEn: '"The safety of every individual among us is our true success."',
      quoteAuthorAr: '- فريق السلامة',
      quoteAuthorEn: '- Safety Team',
      image: 'https://images.unsplash.com/photo-1581094794329-c8112a89af12?auto=format&fit=crop&w=800&q=80',
      color: 'from-red-600 to-red-700',
      bgColor: 'bg-red-50'
    },
    {
      id: 'remote-development',
      icon: FaGlobe,
      titleAr: 'التنمية الاقتصادية للمناطق النائية',
      titleEn: 'Economic Development of Remote Areas',
      subtitleAr: 'بناء الفرص في كل مكان',
      subtitleEn: 'Building Opportunities Everywhere',
      descriptionAr: 'نلتزم بتحريك عجلة الاقتصاد في المناطق النائية من خلال مشروعات استراتيجية تخلق حياة أفضل للمجتمعات البعيدة.',
      descriptionEn: 'We are committed to driving the economy in remote areas through strategic projects that create a better life for distant communities.',
      achievementsAr: [
        'تنفيذ مشروعات بنية تحتية في القرى والمراكز البعيدة',
        'تحفيز الاقتصاد المحلي عبر تشغيل موردين وخدمات محلية',
        'دعم المبادرات الصغيرة والمشروعات المتوسطة',
        'نقل الخبرات والمهارات لسكان المناطق النائية',
        'تحسين فرص العيش والاستقرار للأسر في تلك المناطق',
        'الاستدامة في تحقيق أثر اقتصادي دائم'
      ],
      achievementsEn: [
        'Implementing infrastructure projects in remote villages and centers',
        'Stimulating local economy through local suppliers and services',
        'Supporting small initiatives and medium projects',
        'Transferring expertise and skills to residents of remote areas',
        'Improving living and stability opportunities for families in those areas',
        'Sustainability in achieving lasting economic impact'
      ],
      quoteAr: '"نؤمن أن لكل مجتمع حق في التنمية والازدهار."',
      quoteEn: '"We believe that every community has the right to development and prosperity."',
      quoteAuthorAr: '- فريق التنمية',
      quoteAuthorEn: '- Development Team',
             image: 'https://images.unsplash.com/photo-1518837695005-2083093ee35b?auto=format&fit=crop&w=800&q=80',
       color: 'from-orange-600 to-orange-700',
       bgColor: 'bg-orange-50'
     },
     {
       id: 'mobility-connectivity',
       icon: FaRoad,
       titleAr: 'تسهيل الحركة وربط المجتمعات',
       titleEn: 'Mobility & Community Connectivity',
       subtitleAr: 'ربط المدن والمجتمعات',
       subtitleEn: 'Connecting Cities & Communities',
       descriptionAr: 'من خلال تنفيذ وتمهيد الطرق الجديدة والمساهمة في تطوير شبكة النقل الحديث، ساعدنا في تقريب المسافات وتسهيل الانتقال بين المدن والقرى، مما رفع جودة الحياة وسهل على المواطنين الانتقال والعمل والتعليم.',
       descriptionEn: 'Through implementing and paving new roads and contributing to the development of modern transportation networks, we have helped bridge distances and facilitate movement between cities and villages, improving quality of life and making it easier for citizens to commute, work, and study.',
       achievementsAr: [
         'إنشاء وتطوير شبكات طرق استراتيجية تربط المحافظات',
         'تسهيل حركة البضائع والمسافرين بين مختلف المناطق',
         'تقليل حوادث الطرق بفضل جودة التنفيذ والتخطيط',
         'دعم التنمية الحضرية والريفية عبر ربط المناطق النائية',
         'تسهيل وصول الخدمات (تعليم، صحة، عمل) للمجتمعات المحرومة',
         'تحفيز الاستثمار من خلال بنية تحتية متطورة'
       ],
       achievementsEn: [
         'Creating and developing strategic road networks connecting governorates',
         'Facilitating movement of goods and passengers between different areas',
         'Reducing road accidents thanks to quality implementation and planning',
         'Supporting urban and rural development by connecting remote areas',
         'Facilitating access to services (education, health, work) for deprived communities',
         'Stimulating investment through advanced infrastructure'
       ],
       quoteAr: '"كل طريق نمهدّه هو جسر جديد للأمل والتنمية."',
       quoteEn: '"Every road we pave is a new bridge for hope and development."',
       quoteAuthorAr: '- فريق الطرق والجسور',
       quoteAuthorEn: '- Roads & Bridges Team',
       image: 'https://images.unsplash.com/photo-1545454675-3531b543be5d?auto=format&fit=crop&w=800&q=80',
       color: 'from-indigo-600 to-indigo-700',
       bgColor: 'bg-indigo-50'
     },
     {
       id: 'modern-transportation',
       icon: FaTrain,
       titleAr: 'قيادة مستقبل النقل الحديث',
       titleEn: 'Leading the Future of Modern Transportation',
       subtitleAr: 'المساهمة في مشروعات القطارات والقطار السريع',
       subtitleEn: 'Contributing to Railway & High-Speed Train Projects',
       descriptionAr: 'لعبنا دورًا رئيسيًا في إنشاء خطوط السكك الحديدية الجديدة والمساهمة في تنفيذ القطار السريع، ما وضع مصر على خريطة النقل الذكي وساعد في تقليل زمن الرحلات وتحسين البيئة.',
       descriptionEn: 'We played a major role in creating new railway lines and contributing to the implementation of high-speed trains, putting Egypt on the smart transportation map and helping reduce travel time and improve the environment.',
       achievementsAr: [
         'تنفيذ أعمال البنية التحتية لخطوط السكك الحديدية',
         'استخدام تقنيات حديثة لضمان السلامة وسرعة التنفيذ',
         'دعم المشاريع القومية الكبرى (القطار الكهربائي السريع)',
         'تقليل استهلاك الوقود وانبعاثات الكربون',
         'نقل التكنولوجيا والمعرفة الحديثة للسوق المحلي',
         'توفير خيارات نقل آمنة وسريعة للمواطنين'
       ],
       achievementsEn: [
         'Implementing infrastructure works for railway lines',
         'Using modern technologies to ensure safety and speed of implementation',
         'Supporting major national projects (electric high-speed train)',
         'Reducing fuel consumption and carbon emissions',
         'Transferring modern technology and knowledge to the local market',
         'Providing safe and fast transportation options for citizens'
       ],
       quoteAr: '"نحن نصنع خطوطًا تربط الحاضر بالمستقبل."',
       quoteEn: '"We create lines that connect the present with the future."',
       quoteAuthorAr: '- فريق السكك الحديدية',
       quoteAuthorEn: '- Railway Team',
       image: 'https://images.unsplash.com/photo-1545454675-3531b543be5d?auto=format&fit=crop&w=800&q=80',
       color: 'from-teal-600 to-teal-700',
       bgColor: 'bg-teal-50'
     }
   ];

  return (
    <section className={`section-padding bg-white ${isRTL ? 'font-cairo' : 'font-montserrat'}`} dir={isRTL ? 'rtl' : 'ltr'}>
      <div className="container-custom">
        {/* Section Header */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          viewport={{ once: true }}
          className="text-center mb-16"
        >
          <motion.span
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            transition={{ delay: 0.2, duration: 0.5 }}
            className="inline-block px-4 py-2 bg-primary-100 text-primary-700 rounded-full text-sm font-medium mb-4"
          >
            {language === 'ar' ? 'تأثيرنا في المجتمع' : 'Our Impact in Society'}
          </motion.span>
          
          <motion.h2
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3, duration: 0.8 }}
            className="text-3xl md:text-4xl lg:text-5xl font-bold text-gray-900 mb-6"
          >
            {language === 'ar' ? 'نقوم ببناء' : 'We Build'}
            <span className="text-gradient"> {language === 'ar' ? 'المستقبل معاً' : 'the Future Together'}</span>
          </motion.h2>
          
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.4, duration: 0.8 }}
            className="text-lg text-gray-600 max-w-4xl mx-auto"
          >
            {language === 'ar'
              ? 'نؤمن بأن دورنا يتجاوز البناء والتشييد، فنحن نستثمر في المجتمع ونطور البنية التحتية البشرية والاجتماعية'
              : 'We believe our role goes beyond construction and contracting, as we invest in society and develop human and social infrastructure'}
          </motion.p>
        </motion.div>

        {/* Impact Content */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 lg:gap-12">
          {/* Column 1: Vertical Tabs (Desktop) / Horizontal Tabs (Mobile) */}
          <div className="lg:col-span-1 order-2 lg:order-1">
            {/* Mobile Horizontal Tabs */}
            <div className="lg:hidden mb-6">
              <div className="flex space-x-2 rtl:space-x-reverse overflow-x-auto pb-4 scrollbar-hide -mx-4 px-4">
                {impactData.map((item, index) => (
                  <motion.button
                    key={item.id}
                    initial={{ opacity: 0, x: -20 }}
                    whileInView={{ opacity: 1, x: 0 }}
                    transition={{ delay: index * 0.1, duration: 0.5 }}
                    onClick={() => setActiveTab(index)}
                    className={`flex-shrink-0 flex items-center space-x-2 rtl:space-x-reverse px-3 py-2 rounded-lg transition-all duration-300 text-xs sm:text-sm ${
                      activeTab === index
                        ? 'bg-gradient-to-r from-primary-600 to-primary-700 text-white shadow-lg'
                        : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                    }`}
                  >
                    <item.icon className="w-4 h-4 sm:w-5 sm:h-5" />
                    <span className="font-medium whitespace-nowrap">
                      {isRTL ? item.titleAr : item.titleEn}
                    </span>
                  </motion.button>
                ))}
              </div>
            </div>

            {/* Desktop Vertical Tabs */}
            <div className="hidden lg:block">
              <div className="space-y-2">
                {impactData.map((item, index) => (
                  <motion.button
                    key={item.id}
                    initial={{ opacity: 0, x: -30 }}
                    whileInView={{ opacity: 1, x: 0 }}
                    transition={{ delay: index * 0.1, duration: 0.5 }}
                    onClick={() => setActiveTab(index)}
                    className={`w-full flex items-center space-x-3 rtl:space-x-reverse p-3 rounded-xl transition-all duration-300 text-left ${
                      activeTab === index
                        ? 'bg-gradient-to-r from-primary-600 to-primary-700 text-white shadow-lg transform scale-105'
                        : 'bg-gray-50 text-gray-700 hover:bg-gray-100 hover:scale-102'
                    }`}
                  >
                    <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${
                      activeTab === index ? 'bg-white/20' : 'bg-white shadow-sm'
                    }`}>
                      <item.icon className={`w-5 h-5 ${activeTab === index ? 'text-white' : 'text-primary-600'}`} />
                    </div>
                    <div className="min-w-0 flex-1">
                      <div className="font-semibold text-sm truncate">
                        {isRTL ? item.titleAr : item.titleEn}
                      </div>
                      <div className={`text-xs truncate ${activeTab === index ? 'text-white/80' : 'text-gray-500'}`}>
                        {isRTL ? item.subtitleAr : item.subtitleEn}
                      </div>
                    </div>
                  </motion.button>
                ))}
              </div>
            </div>
          </div>

          {/* Column 2: Content */}
          <div className="lg:col-span-1 order-3 lg:order-2">
            <AnimatePresence mode="wait">
              <motion.div
                key={activeTab}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -20 }}
                transition={{ duration: 0.5 }}
                className="h-full"
              >
                <div className="bg-white rounded-2xl p-4 sm:p-6 lg:p-8 shadow-lg border border-gray-100 h-full">
                  <div className="mb-4 sm:mb-6">
                    <h3 className="text-xl sm:text-2xl lg:text-3xl font-bold text-gray-900 mb-2">
                      {isRTL ? impactData[activeTab].titleAr : impactData[activeTab].titleEn}
                    </h3>
                    <p className="text-base sm:text-lg text-primary-600 font-medium mb-3 sm:mb-4">
                      {isRTL ? impactData[activeTab].subtitleAr : impactData[activeTab].subtitleEn}
                    </p>
                    <p className="text-sm sm:text-base text-gray-600 leading-relaxed">
                      {isRTL ? impactData[activeTab].descriptionAr : impactData[activeTab].descriptionEn}
                    </p>
                  </div>

                  {/* Achievements */}
                  <div className="mb-4 sm:mb-6">
                    <h4 className="text-base sm:text-lg font-semibold text-gray-900 mb-3 sm:mb-4">
                      {language === 'ar' ? 'إنجازاتنا الرئيسية' : 'Key Achievements'}
                    </h4>
                    <div className="space-y-2 sm:space-y-3">
                      {(isRTL ? impactData[activeTab].achievementsAr : impactData[activeTab].achievementsEn).map((achievement, index) => (
                        <motion.div
                          key={index}
                          initial={{ opacity: 0, x: -10 }}
                          animate={{ opacity: 1, x: 0 }}
                          transition={{ delay: index * 0.1, duration: 0.3 }}
                          className="flex items-start space-x-2 rtl:space-x-reverse sm:space-x-3"
                        >
                          <div className="w-1.5 h-1.5 sm:w-2 sm:h-2 bg-primary-500 rounded-full mt-1.5 sm:mt-2 flex-shrink-0"></div>
                          <span className="text-xs sm:text-sm text-gray-700 leading-relaxed">{achievement}</span>
                        </motion.div>
                      ))}
                    </div>
                  </div>

                  {/* Quote */}
                  <div className={`${impactData[activeTab].bgColor} rounded-xl p-3 sm:p-4 border-l-4 border-primary-500`}>
                    <blockquote className="text-xs sm:text-sm text-gray-700 italic mb-2">
                      "{isRTL ? impactData[activeTab].quoteAr : impactData[activeTab].quoteEn}"
                    </blockquote>
                    <cite className="text-primary-600 font-medium text-xs">
                      {isRTL ? impactData[activeTab].quoteAuthorAr : impactData[activeTab].quoteAuthorEn}
                    </cite>
                  </div>
                </div>
              </motion.div>
            </AnimatePresence>
          </div>

          {/* Column 3: Image */}
          <div className="lg:col-span-1 order-1 lg:order-3">
            <AnimatePresence mode="wait">
              <motion.div
                key={activeTab}
                initial={{ opacity: 0, scale: 0.95 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0, scale: 1.05 }}
                transition={{ duration: 0.5 }}
                className="relative h-64 sm:h-80 lg:h-full min-h-[300px] sm:min-h-[400px] lg:min-h-[500px] rounded-2xl overflow-hidden shadow-lg"
              >
                <img
                  src={impactData[activeTab].image}
                  alt={isRTL ? impactData[activeTab].titleAr : impactData[activeTab].titleEn}
                  className="w-full h-full object-cover"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent"></div>
                
                {/* Floating Stats */}
                <div className="absolute bottom-4 sm:bottom-6 left-4 sm:left-6 right-4 sm:right-6">
                  <div className="bg-white/95 backdrop-blur-sm rounded-xl p-3 sm:p-4">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center space-x-2 rtl:space-x-reverse sm:space-x-3 min-w-0 flex-1">
                        <div className={`w-8 h-8 sm:w-10 sm:h-10 bg-gradient-to-r ${impactData[activeTab].color} rounded-lg flex items-center justify-center flex-shrink-0`}>
                          {React.createElement(impactData[activeTab].icon, { className: "w-4 h-4 sm:w-5 sm:h-5 text-white" })}
                        </div>
                        <div className="min-w-0 flex-1">
                          <div className="text-xs sm:text-sm font-semibold text-gray-900 truncate">
                            {isRTL ? impactData[activeTab].titleAr : impactData[activeTab].titleEn}
                          </div>
                          <div className="text-xs text-gray-600">
                            {language === 'ar' ? 'تأثير إيجابي' : 'Positive Impact'}
                          </div>
                        </div>
                      </div>
                      <div className="text-right flex-shrink-0">
                        <div className="text-lg sm:text-2xl font-bold text-primary-600">
                          {(activeTab + 1) * 25}%
                        </div>
                        <div className="text-xs text-gray-600">
                          {language === 'ar' ? 'تحسن' : 'Improvement'}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </motion.div>
            </AnimatePresence>
          </div>
        </div>
      </div>
    </section>
  );
};

export default ImpactSection; 